import { supabase } from './supabase';

const studioApplicationService = {
  // Submit a new studio application
  submitApplication: async (applicationData) => {
    try {
      console.log('🔍 Getting authenticated user...');
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.error('❌ No authenticated user found');
        return { success: false, error: 'User not authenticated' };
      }

      console.log('✅ User authenticated:', user.id);

      const insertData = {
        user_id: user.id,
        studio_name: applicationData.studioName,
        studio_website: applicationData.studioWebsite || null,
        studio_description: applicationData.studioDescription,
        team_size: applicationData.teamSize,
        status: 'pending'
      };

      console.log('📝 Inserting application data:', insertData);

      const { data, error } = await supabase
        .from('studio_applications')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('❌ Database error:', error);
        if (error.code === '23505') { // Unique constraint violation
          return { success: false, error: 'You have already submitted a studio application' };
        }
        return { success: false, error: error.message };
      }

      console.log('✅ Application inserted successfully:', data);
      return { success: true, data };
    } catch (error) {
      console.error('❌ Exception in submitApplication:', error);
      return { success: false, error: 'Failed to submit application. Please try again.' };
    }
  },

  // Get current user's application
  getMyApplication: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      const { data, error } = await supabase
        .from('studio_applications')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // No rows returned
          return { success: true, data: null };
        }
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch application.' };
    }
  },

  // Get all applications (admin only)
  getAllApplications: async (status = null) => {
    try {
      let query = supabase
        .from('studio_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data: applications, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      // Fetch user profiles separately for each application
      const applicationsWithUsers = await Promise.all(
        (applications || []).map(async (app) => {
          const { data: userProfile } = await supabase
            .from('user_profiles')
            .select('id, email, full_name')
            .eq('id', app.user_id)
            .single();

          let reviewerProfile = null;
          if (app.reviewed_by) {
            const { data: reviewer } = await supabase
              .from('user_profiles')
              .select('id, email, full_name')
              .eq('id', app.reviewed_by)
              .single();
            reviewerProfile = reviewer;
          }

          return {
            ...app,
            user: userProfile,
            reviewer: reviewerProfile
          };
        })
      );

      return { success: true, data: applicationsWithUsers };
    } catch (error) {
      return { success: false, error: 'Failed to fetch applications.' };
    }
  },

  // Approve application (admin only)
  approveApplication: async (applicationId) => {
    try {
      const { data, error } = await supabase.rpc('approve_studio_application', {
        application_id: applicationId
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data || !data.success) {
        return { success: false, error: data?.error || 'Failed to approve application' };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to approve application. Please try again.' };
    }
  },

  // Reject application (admin only)
  rejectApplication: async (applicationId, reason) => {
    try {
      const { data, error } = await supabase.rpc('reject_studio_application', {
        application_id: applicationId,
        reason: reason
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data || !data.success) {
        return { success: false, error: data?.error || 'Failed to reject application' };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to reject application. Please try again.' };
    }
  },

  // Get application statistics (admin only)
  getApplicationStats: async () => {
    try {
      const { data, error } = await supabase
        .from('studio_applications')
        .select('status');

      if (error) {
        return { success: false, error: error.message };
      }

      const stats = {
        total: data.length,
        pending: data.filter(app => app.status === 'pending').length,
        approved: data.filter(app => app.status === 'approved').length,
        rejected: data.filter(app => app.status === 'rejected').length
      };

      return { success: true, data: stats };
    } catch (error) {
      return { success: false, error: 'Failed to fetch statistics.' };
    }
  }
};

export default studioApplicationService;
